const RTL_LANGUAGE_PREFIXES = ['ar', 'fa', 'he', 'ur', 'ps', 'dv', 'ku', 'yi'];

export const DEFAULT_WIDGET_STRINGS = {
  dialogAriaLabel: '{company} chat',
  onlineWithCompany: 'Online · {company}',
  settingsLink: 'Settings',
  sessions: 'Sessions',
  minimizeChat: 'Minimize',
  moreOptions: 'More options',
  exportOptions: 'Export options',
  exportAsJson: 'Export as JSON',
  exportAsText: 'Export as Text',
  exportAsMarkdown: 'Export as Markdown',
  loading: 'Loading...',
  chatMessages: 'Chat messages',
  emptyState: 'Say hi to start the conversation.',
  scrollToLatest: 'Scroll to latest messages',
  latest: 'Latest',
  attachFiles: 'Attach files',
  startVoiceInput: 'Start voice input',
  startVideoChat: 'Start video chat',
  messagePlaceholder: 'Message...',
  messageInputAria: 'Message input',
  messageInputHelp: 'Press Enter to send. Press Shift and Enter for a new line.',
  sendMessage: 'Send message',
  removeAttachment: 'Remove attachment {name}',
  domainBlockedStatus: 'Chat is disabled for this domain.',
  messageLimitStatus: 'You have reached the message limit for this session.',
  offlineStatus: 'We are currently offline.',
  preChatRequiredError: 'Complete the pre-chat form to start chatting.',
  consentRequiredError: 'Please accept the consent to continue.',
  consentCheckboxLabel: 'Chat consent',
  consentDefaultText: 'By starting this chat, you agree to our terms.',
  consentLearnMore: 'Learn more',
  rateLimitedError: "You're sending too fast. Try again in {seconds}s.",
  openChat: 'Open chat',
  openChatUnread: 'Open chat ({count} unread message{plural})',
  liveRegionUnread: 'You have {count} unread message{plural}.',
  dropFilesToUpload: 'Drop files to upload',
  yourMessageAria: 'Your message',
  assistantMessageAria: 'Assistant message',
  sourcesHeading: 'Sources:',
  copyMessage: 'Copy message',
  goodResponse: 'Good response',
  thumbsUp: 'Thumbs up',
  notHelpful: 'Not helpful',
  thumbsDown: 'Thumbs down',
  agentTypingAria: 'Agent is typing',
  runningTools: 'Running tools...',
  selectOption: 'Select an option',
  startChat: 'Start chat',
  submit: 'Submit',
  fieldRequired: '{field} is required.',
  sessionsTitle: 'Sessions',
  closeSessions: 'Close sessions',
  newSession: 'New session',
  sessionsTotal: '{count} total',
  noSessionsYet: 'No sessions yet',
  newConversation: 'New conversation',
  sessionMessageCount: '{count} messages',
  deleteSession: 'Delete session',
  devTelemetry: 'Dev telemetry',
  refresh: 'Refresh',
  loadingTelemetry: 'Loading telemetry...',
  noTelemetry: 'No telemetry available yet.',
  telemetryRequests: 'Requests',
  telemetryErrors: 'Errors',
  telemetryAvgLatency: 'Avg latency',
  telemetryLastLatency: 'Last latency',
  telemetryKbHitRate: 'KB hit rate',
  telemetryTokensAvg: 'Tokens (avg)',
  telemetryLastRequest: 'Last request: {provider} / {model} / {mode}',
  telemetryModeStream: 'stream',
  telemetryModeSync: 'sync',
  telemetryAttributes: 'Attributes',
  telemetryRules: 'Rules',
  telemetryExamples: 'Examples',
  telemetryKbDocs: 'KB docs',
  none: 'None',
  orderDetails: 'Order details',
  viewOrderHistory: 'View order history',
  hideOrderHistory: 'Hide order history',
  orderHistory: 'Order History',
  previous: 'Previous',
  next: 'Next',
  agentReasoning: 'Agent Reasoning',
  expandReasoning: 'Expand agent reasoning',
  collapseReasoning: 'Collapse agent reasoning',
} as const;

export type WidgetStringKey = keyof typeof DEFAULT_WIDGET_STRINGS;
export type WidgetStrings = Record<WidgetStringKey, string>;

const SPANISH_WIDGET_STRINGS: Partial<WidgetStrings> = {
  dialogAriaLabel: 'Chat de {company}',
  onlineWithCompany: 'En línea · {company}',
  settingsLink: 'Configuración',
  sessions: 'Sesiones',
  minimizeChat: 'Minimizar',
  moreOptions: 'Más opciones',
  exportOptions: 'Opciones de exportación',
  exportAsJson: 'Exportar como JSON',
  exportAsText: 'Exportar como texto',
  exportAsMarkdown: 'Exportar como Markdown',
  loading: 'Cargando...',
  chatMessages: 'Mensajes del chat',
  emptyState: 'Di hola para iniciar la conversación.',
  scrollToLatest: 'Ir a los últimos mensajes',
  latest: 'Últimos',
  attachFiles: 'Adjuntar archivos',
  startVoiceInput: 'Iniciar entrada de voz',
  startVideoChat: 'Iniciar videollamada',
  messagePlaceholder: 'Mensaje...',
  messageInputAria: 'Entrada del mensaje',
  messageInputHelp: 'Presiona Enter para enviar. Presiona Shift y Enter para una nueva línea.',
  sendMessage: 'Enviar mensaje',
  removeAttachment: 'Quitar adjunto {name}',
  domainBlockedStatus: 'El chat está deshabilitado para este dominio.',
  messageLimitStatus: 'Has alcanzado el límite de mensajes para esta sesión.',
  offlineStatus: 'Actualmente estamos fuera de línea.',
  preChatRequiredError: 'Completa el formulario previo para comenzar a chatear.',
  consentRequiredError: 'Acepta el consentimiento para continuar.',
  consentCheckboxLabel: 'Consentimiento de chat',
  consentDefaultText: 'Al iniciar este chat, aceptas nuestros términos.',
  consentLearnMore: 'Saber más',
  rateLimitedError: 'Estás enviando muy rápido. Inténtalo de nuevo en {seconds}s.',
  openChat: 'Abrir chat',
  openChatUnread: 'Abrir chat ({count} mensaje{plural} sin leer)',
  liveRegionUnread: 'Tienes {count} mensaje{plural} sin leer.',
  dropFilesToUpload: 'Suelta archivos para cargarlos',
  yourMessageAria: 'Tu mensaje',
  assistantMessageAria: 'Mensaje del asistente',
  sourcesHeading: 'Fuentes:',
  copyMessage: 'Copiar mensaje',
  goodResponse: 'Buena respuesta',
  thumbsUp: 'Pulgar arriba',
  notHelpful: 'No útil',
  thumbsDown: 'Pulgar abajo',
  agentTypingAria: 'El agente está escribiendo',
  runningTools: 'Ejecutando herramientas...',
  selectOption: 'Selecciona una opción',
  startChat: 'Iniciar chat',
  submit: 'Enviar',
  fieldRequired: '{field} es obligatorio.',
  sessionsTitle: 'Sesiones',
  closeSessions: 'Cerrar sesiones',
  newSession: 'Nueva sesión',
  sessionsTotal: '{count} en total',
  noSessionsYet: 'Aún no hay sesiones',
  newConversation: 'Nueva conversación',
  sessionMessageCount: '{count} mensajes',
  deleteSession: 'Eliminar sesión',
  devTelemetry: 'Telemetría de desarrollo',
  refresh: 'Actualizar',
  loadingTelemetry: 'Cargando telemetría...',
  noTelemetry: 'Aún no hay telemetría disponible.',
  telemetryRequests: 'Solicitudes',
  telemetryErrors: 'Errores',
  telemetryAvgLatency: 'Latencia promedio',
  telemetryLastLatency: 'Última latencia',
  telemetryKbHitRate: 'Tasa de aciertos KB',
  telemetryTokensAvg: 'Tokens (promedio)',
  telemetryLastRequest: 'Última solicitud: {provider} / {model} / {mode}',
  telemetryModeStream: 'stream',
  telemetryModeSync: 'sync',
  telemetryAttributes: 'Atributos',
  telemetryRules: 'Reglas',
  telemetryExamples: 'Ejemplos',
  telemetryKbDocs: 'Documentos KB',
  none: 'Ninguno',
  orderDetails: 'Detalles del pedido',
  viewOrderHistory: 'Ver historial de pedidos',
  hideOrderHistory: 'Ocultar historial de pedidos',
  orderHistory: 'Historial de pedidos',
  previous: 'Anterior',
  next: 'Siguiente',
  agentReasoning: 'Razonamiento del agente',
  expandReasoning: 'Expandir razonamiento del agente',
  collapseReasoning: 'Colapsar razonamiento del agente',
};

const ARABIC_WIDGET_STRINGS: Partial<WidgetStrings> = {
  dialogAriaLabel: 'دردشة {company}',
  onlineWithCompany: 'متصل · {company}',
  settingsLink: 'الإعدادات',
  sessions: 'الجلسات',
  minimizeChat: 'تصغير',
  moreOptions: 'خيارات إضافية',
  exportOptions: 'خيارات التصدير',
  exportAsJson: 'تصدير كـ JSON',
  exportAsText: 'تصدير كنص',
  exportAsMarkdown: 'تصدير كـ Markdown',
  loading: 'جارٍ التحميل...',
  chatMessages: 'رسائل الدردشة',
  emptyState: 'ابدأ برسالة لبدء المحادثة.',
  scrollToLatest: 'الانتقال إلى أحدث الرسائل',
  latest: 'الأحدث',
  attachFiles: 'إرفاق ملفات',
  startVoiceInput: 'بدء إدخال صوتي',
  startVideoChat: 'بدء دردشة فيديو',
  messagePlaceholder: 'رسالة...',
  messageInputAria: 'حقل الرسالة',
  messageInputHelp: 'اضغط Enter للإرسال. اضغط Shift و Enter لسطر جديد.',
  sendMessage: 'إرسال الرسالة',
  removeAttachment: 'إزالة المرفق {name}',
  domainBlockedStatus: 'الدردشة معطلة لهذا النطاق.',
  messageLimitStatus: 'لقد وصلت إلى حد الرسائل لهذه الجلسة.',
  offlineStatus: 'نحن غير متصلين حاليًا.',
  preChatRequiredError: 'أكمل نموذج ما قبل الدردشة لبدء المحادثة.',
  consentRequiredError: 'يرجى قبول الموافقة للمتابعة.',
  consentCheckboxLabel: 'موافقة الدردشة',
  consentDefaultText: 'ببدء هذه الدردشة، فإنك توافق على الشروط.',
  consentLearnMore: 'معرفة المزيد',
  rateLimitedError: 'أنت ترسل بسرعة كبيرة. حاول مرة أخرى خلال {seconds}ث.',
  openChat: 'فتح الدردشة',
  openChatUnread: 'فتح الدردشة ({count} رسالة غير مقروءة)',
  liveRegionUnread: 'لديك {count} رسائل غير مقروءة.',
  dropFilesToUpload: 'أفلت الملفات للتحميل',
  yourMessageAria: 'رسالتك',
  assistantMessageAria: 'رسالة المساعد',
  sourcesHeading: 'المصادر:',
  copyMessage: 'نسخ الرسالة',
  goodResponse: 'إجابة جيدة',
  thumbsUp: 'إعجاب',
  notHelpful: 'غير مفيد',
  thumbsDown: 'عدم إعجاب',
  agentTypingAria: 'الوكيل يكتب الآن',
  runningTools: 'جاري تشغيل الأدوات...',
  selectOption: 'اختر خيارًا',
  startChat: 'بدء الدردشة',
  submit: 'إرسال',
  fieldRequired: '{field} مطلوب.',
  sessionsTitle: 'الجلسات',
  closeSessions: 'إغلاق الجلسات',
  newSession: 'جلسة جديدة',
  sessionsTotal: '{count} إجماليًا',
  noSessionsYet: 'لا توجد جلسات بعد',
  newConversation: 'محادثة جديدة',
  sessionMessageCount: '{count} رسالة',
  deleteSession: 'حذف الجلسة',
  devTelemetry: 'قياسات المطور',
  refresh: 'تحديث',
  loadingTelemetry: 'جارٍ تحميل القياسات...',
  noTelemetry: 'لا توجد قياسات متاحة بعد.',
  telemetryRequests: 'الطلبات',
  telemetryErrors: 'الأخطاء',
  telemetryAvgLatency: 'متوسط زمن الاستجابة',
  telemetryLastLatency: 'آخر زمن استجابة',
  telemetryKbHitRate: 'معدل إصابة KB',
  telemetryTokensAvg: 'متوسط الرموز',
  telemetryLastRequest: 'آخر طلب: {provider} / {model} / {mode}',
  telemetryModeStream: 'متدفق',
  telemetryModeSync: 'متزامن',
  telemetryAttributes: 'السمات',
  telemetryRules: 'القواعد',
  telemetryExamples: 'الأمثلة',
  telemetryKbDocs: 'مستندات KB',
  none: 'لا يوجد',
  orderDetails: 'تفاصيل الطلب',
  viewOrderHistory: 'عرض سجل الطلبات',
  hideOrderHistory: 'إخفاء سجل الطلبات',
  orderHistory: 'سجل الطلبات',
  previous: 'السابق',
  next: 'التالي',
  agentReasoning: 'تفكير الوكيل',
  expandReasoning: 'توسيع تفكير الوكيل',
  collapseReasoning: 'طي تفكير الوكيل',
};

const LOCALE_STRING_PACKS: Record<string, Partial<WidgetStrings>> = {
  en: {},
  es: SPANISH_WIDGET_STRINGS,
  ar: ARABIC_WIDGET_STRINGS,
};

function normalizeLanguage(language?: string | null): string {
  return String(language || 'en')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
}

function getLanguageCandidates(language?: string | null): string[] {
  const normalized = normalizeLanguage(language);
  const [base] = normalized.split('-');
  if (base && base !== normalized) return [normalized, base];
  return [normalized];
}

function sanitizeOverrides(overrides?: Record<string, unknown> | null): Partial<WidgetStrings> {
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) return {};
  const next: Partial<WidgetStrings> = {};
  (Object.keys(DEFAULT_WIDGET_STRINGS) as WidgetStringKey[]).forEach(key => {
    const value = overrides[key];
    if (typeof value === 'string' && value.trim()) {
      next[key] = value;
    }
  });
  return next;
}

export function resolveWidgetStrings(
  language?: string | null,
  overrides?: Record<string, unknown> | null
): WidgetStrings {
  const candidates = getLanguageCandidates(language);
  const localePack =
    candidates.map(code => LOCALE_STRING_PACKS[code]).find(Boolean) || LOCALE_STRING_PACKS.en;
  return {
    ...(DEFAULT_WIDGET_STRINGS as WidgetStrings),
    ...(localePack || {}),
    ...sanitizeOverrides(overrides),
  };
}

export function formatWidgetString(
  template: string,
  values: Record<string, string | number | null | undefined>
): string {
  return template.replace(/\{(\w+)\}/g, (_match, key) => {
    const value = values[key];
    if (value === null || value === undefined) return '';
    return String(value);
  });
}

export function isRtlLanguage(language?: string | null): boolean {
  const [base] = normalizeLanguage(language).split('-');
  return RTL_LANGUAGE_PREFIXES.includes(base);
}

export function resolveTextDirection(
  language?: string | null,
  preferred?: 'ltr' | 'rtl' | 'auto'
): 'ltr' | 'rtl' {
  if (preferred === 'ltr' || preferred === 'rtl') return preferred;
  return isRtlLanguage(language) ? 'rtl' : 'ltr';
}
